import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import PaymentMethodCard from "../components/paymentMethods/PaymentMethodCard";
import CreatePaymentMethodModal from "../components/paymentMethods/CreatePaymentMethodModal";
import UpdatePaymentMethodModal from "../components/paymentMethods/UpdatePaymentMethodModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import { usePermissions } from "../hooks/usePermissions";
import {
  getPaymentMethods,
  deletePaymentMethod,
  resetPaymentMethodServerStatus,
} from "../redux/actions/paymentMethodActions";
import PageError from "../components/common/PageError";

const PaymentMethodsPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();

  const {
    paymentMethods,
    loadingPaymentMethods,
    errorLoadingPaymentMethods,
    serverStatus,
    loadingPaymentMethodDelete,
    loadingPaymentMethodUpdate,
    loadingPaymentMethodCreate,
    errorCreatingPaymentMethod,
    errorUpdatingPaymentMethod,
  } = useSelector((state) => state.paymentMethod);

  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);

  const canCreate = hasPermission("payment_method:create");
  const canUpdate = hasPermission("payment_method:update");
  const canDelete = hasPermission("payment_method:delete");

  const fetchMethods = useCallback(() => {
    dispatch(getPaymentMethods());
  }, [dispatch]);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  const filteredMethods = useMemo(() => {
    if (!paymentMethods) return [];
    if (!searchTerm.trim()) return paymentMethods;

    const query = searchTerm.toLowerCase();
    return paymentMethods.filter(
      (method) =>
        method.name.toLowerCase().includes(query) ||
        method.provider?.toLowerCase().includes(query),
    );
  }, [paymentMethods, searchTerm]);

  const handleOpenCreate = () => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(
    () => !loadingPaymentMethodCreate && setIsCreateModalOpen(false),
    [loadingPaymentMethodCreate],
  );

  const handleOpenUpdate = (method) => {
    setSelectedMethod(method);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingPaymentMethodUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedMethod(null);
    }
  }, [loadingPaymentMethodUpdate]);

  const handleOpenDelete = (method) => {
    setSelectedMethod(method);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingPaymentMethodDelete) {
      setIsDeleteModalOpen(false);
      setSelectedMethod(null);
    }
  }, [loadingPaymentMethodDelete]);

  const handleDeleteConfirm = () => {
    if (selectedMethod) dispatch(deletePaymentMethod(selectedMethod.id));
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(resetPaymentMethodServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = () => {
    if (loadingPaymentMethods) return <AppCircularProgress />;
    if (errorLoadingPaymentMethods)
      return (
        <PageError error={errorLoadingPaymentMethods} onClick={fetchMethods} />
      );

    if (filteredMethods.length === 0)
      return (
        <Box textAlign="center" mt={5}>
          <Typography color="text.secondary">
            {searchTerm
              ? `No results for "${searchTerm}"`
              : "No payment methods found."}
          </Typography>
        </Box>
      );

    return (
      <Grid container spacing={3}>
        {filteredMethods.map((method) => (
          <Grid item key={method.id} xs={12} sm={6} md={4} lg={3}>
            <PaymentMethodCard
              method={method}
              onEdit={canUpdate ? () => handleOpenUpdate(method) : undefined}
              onDelete={canDelete ? () => handleOpenDelete(method) : undefined}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <PageContainer
      title="Payment Methods"
      subtitle="Manage how customers pay you and link methods to financial accounts."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Method
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search methods..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreatePaymentMethodModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingPaymentMethodCreate}
          error={errorCreatingPaymentMethod}
        />
      )}
      {isUpdateModalOpen && selectedMethod && (
        <UpdatePaymentMethodModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          method={selectedMethod}
          loading={loadingPaymentMethodUpdate}
          error={errorUpdatingPaymentMethod}
        />
      )}
      {isDeleteModalOpen && selectedMethod && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Method"
          message={`Delete "${selectedMethod.name}"?`}
          loading={loadingPaymentMethodDelete}
        />
      )}
    </PageContainer>
  );
};

export default PaymentMethodsPage;
